/**
 * Canonical product, pricing, and brand data for Bounce.
 *
 * This is the single source of truth for anything that surfaces product info
 * to users, AI crawlers, or schema markup — `/llms.txt`, `/about`,
 * SoftwareApplication JSON-LD, Organization JSON-LD, and similar.
 *
 * If a price changes, it changes here and flows to every SEO/GEO surface.
 *
 * Note: marketing UI components (`components/Coach/data.ts`,
 * `screens/ClubLanding/.../PricingSection.tsx`) currently hardcode their
 * own pricing strings — migrate to these constants when convenient.
 *
 * Route paths import from `constants/pages` so a route rename in one
 * place flows everywhere.
 */

// Route paths inlined (instead of `import ... from 'constants/pages'`) to keep
// this file free of React-tainted imports. App Router metadata routes
// (`app/robots.ts`, `app/llms.txt/route.ts`) consume this file at server-build
// time and would otherwise drag `constants/pages.ts`'s component graph (Apollo
// `createContext`) into the route bundle. Keep in sync with `constants/pages.ts`.
const BLOG_PAGE = '/blog';
const CLINIC_FINDER_PAGE = '/clinics';
const CLUB_LANDING_PAGE = '/club';
const COACHES_LANDING_PAGE = '/coaching-software';
const COACH_MARKETPLACE_PAGE = '/lessons';
const COURT_FINDER_PAGE = '/courts';
const FIND_A_PRO_PPR_PAGE = '/find-a-pro-ppr';
const FIND_A_PRO_PTR_PAGE = '/find-a-pro-ptr';
const LEAGUE_MARKETPLACE_PAGE = '/leagues';
const OPEN_PLAY_MARKETPLACE_PAGE = '/open-play';
const SOCIAL_MARKETPLACE_PAGE = '/socials';
const TOURNAMENTS_PAGE = '/tournaments';

export const SITE_URL = 'https://www.bounce.game';

/** `@id` used by the global Organization JSON-LD in `_document.tsx`. */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

/** `@id` used by the global WebSite JSON-LD in `_document.tsx`. */
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const BRAND_NAME = 'Bounce';

export const COMPANY_LEGAL_NAME = 'Advantage Platform Inc.';

export const COMPANY_SUPPORT_EMAIL = 'support@bounce.game';

/**
 * Mailing address. Empty string until filled in by ops/legal — when populated,
 * it surfaces on `/contact` and in the footer.
 * @todo populate
 */
export const COMPANY_MAILING_ADDRESS: string = '';

/**
 * Customer-facing phone number. Empty string until provided.
 * @todo populate
 */
export const COMPANY_PHONE: string = '';

/** Meta Business domain-verification token for bounce.game. Public by design — rendered in `_document.tsx`. */
export const FACEBOOK_DOMAIN_VERIFICATION = 'p1ee7hrl3dr7xcf75i5axvpsry4qvt';

export const BRAND_TAGLINE = 'The #1 app for racket sports.';

export const BRAND_DESCRIPTION =
  'Bounce is the operating system for racket sports. We make coaching software ' +
  "that runs a coach's entire business, club software with a Growth Suite that " +
  'fills programs, and tournament software that handles everything from Saturday ' +
  'round robins to multi-day sanctioned events. Players use the Bounce marketplace ' +
  'to find courts, coaches, lessons, clinics, tournaments, leagues, socials, and ' +
  'open play in their city — across pickleball, tennis, and padel.';

export const FOUNDER_NAME = 'Ryan Van Winkle';

export const FOUNDER_LINKEDIN = 'https://www.linkedin.com/in/nrvanwinkle/';

export interface PricingTier {
  name: string;
  /** Display string, e.g. "$25/mo", "Free", "Contact us". */
  displayPrice: string;
  /** Numeric monthly USD value when applicable, for schema Offer markup. */
  monthlyUsd?: number;
  shortDescription?: string;
}

export interface ProductInfo {
  /** Slug — used for keys, telemetry, and analytics dimensions. Free-form. */
  slug: string;
  name: string;
  path: string;
  url: string;
  shortDescription: string;
  pricing?: PricingTier[];
}

const productUrl = (path: string) => `${SITE_URL}${path}`;

/* --- Software (paid SaaS) --------------------------------------------- */

export const PRODUCT_COACHING_SOFTWARE: ProductInfo = {
  slug: 'coaching-software',
  name: 'Coaching Software',
  path: COACHES_LANDING_PAGE,
  url: productUrl(COACHES_LANDING_PAGE),
  shortDescription:
    "Coach CRM that runs a coach's entire business — calendar, lessons, payments, " +
    'players, group lessons, remote coaching, and a marketplace listing.',
  pricing: [
    { name: 'Basic', displayPrice: 'Free', monthlyUsd: 0 },
    { name: 'Pro', displayPrice: '$25/mo', monthlyUsd: 25 },
    { name: 'Enterprise', displayPrice: 'Contact us' },
  ],
};

export const PRODUCT_CLUB_SOFTWARE: ProductInfo = {
  slug: 'club-software',
  name: 'Club Growth Suite',
  path: CLUB_LANDING_PAGE,
  url: productUrl(CLUB_LANDING_PAGE),
  shortDescription:
    'Grow your racket sports club with a marketplace listing, program management, ' +
    'lead inbox, marketplace boosts, and analytics — built to fill programs and ' +
    'grow membership.',
  pricing: [
    { name: 'Free', displayPrice: '$0/mo', monthlyUsd: 0 },
    { name: 'Growth', displayPrice: '$179/mo', monthlyUsd: 179 },
  ],
};

export const PRODUCT_TOURNAMENTS: ProductInfo = {
  slug: 'tournaments-software',
  name: 'Tournament Software (Gameday)',
  path: TOURNAMENTS_PAGE,
  url: productUrl(TOURNAMENTS_PAGE),
  shortDescription:
    'Free tournament software for organizers — runs everything from Saturday round ' +
    'robins and americano to single/double-elimination brackets, with live scoring, ' +
    'automated standings, and player check-in.',
};

/* --- Marketplace (public, free) --------------------------------------- */

export const MARKETPLACE_COURTS: ProductInfo = {
  slug: 'marketplace-courts',
  name: 'Courts',
  path: COURT_FINDER_PAGE,
  url: productUrl(COURT_FINDER_PAGE),
  shortDescription:
    'Find pickleball, tennis, and padel courts in your city — 3,000+ venues, ' +
    'searchable by city, region, or country.',
};

export const MARKETPLACE_LESSONS: ProductInfo = {
  slug: 'marketplace-lessons',
  name: 'Lessons & Coaches',
  path: COACH_MARKETPLACE_PAGE,
  url: productUrl(COACH_MARKETPLACE_PAGE),
  shortDescription:
    'Find coaches near you — book private lessons and group lessons across ' +
    'pickleball, tennis, and padel.',
};

export const MARKETPLACE_CLINICS: ProductInfo = {
  slug: 'marketplace-clinics',
  name: 'Clinics, Programming & Camps',
  path: CLINIC_FINDER_PAGE,
  url: productUrl(CLINIC_FINDER_PAGE),
  shortDescription:
    'Find clinics, programming, and camps from coaches and clubs in your city.',
};

export const MARKETPLACE_TOURNAMENTS: ProductInfo = {
  slug: 'marketplace-tournaments',
  name: 'Tournaments',
  path: TOURNAMENTS_PAGE,
  url: productUrl(TOURNAMENTS_PAGE),
  shortDescription:
    'Find and register for tournaments near you across pickleball, tennis, and padel.',
};

export const MARKETPLACE_LEAGUES: ProductInfo = {
  slug: 'marketplace-leagues',
  name: 'Leagues',
  path: LEAGUE_MARKETPLACE_PAGE,
  url: productUrl(LEAGUE_MARKETPLACE_PAGE),
  shortDescription:
    'Find leagues in your city for ongoing competitive play across pickleball, ' +
    'tennis, and padel.',
};

export const MARKETPLACE_SOCIALS: ProductInfo = {
  slug: 'marketplace-socials',
  name: 'Socials',
  path: SOCIAL_MARKETPLACE_PAGE,
  url: productUrl(SOCIAL_MARKETPLACE_PAGE),
  shortDescription:
    'Find social play events near you — organized meet-and-play sessions across ' +
    'racket sports.',
};

export const MARKETPLACE_OPEN_PLAY: ProductInfo = {
  slug: 'marketplace-open-play',
  name: 'Open Play',
  path: OPEN_PLAY_MARKETPLACE_PAGE,
  url: productUrl(OPEN_PLAY_MARKETPLACE_PAGE),
  shortDescription:
    'Find drop-in open play sessions in your area across pickleball, tennis, and padel.',
};

/* --- Find a Pro (federated coach directories) ------------------------- */

export const FIND_A_PRO_PPR: ProductInfo = {
  slug: 'find-a-pro-ppr',
  name: 'Find a PPR-Certified Pickleball Pro',
  path: FIND_A_PRO_PPR_PAGE,
  url: productUrl(FIND_A_PRO_PPR_PAGE),
  shortDescription:
    'Find a Professional Pickleball Registry (PPR) certified coach — the top ' +
    'pickleball coaching certification.',
};

export const FIND_A_PRO_PTR: ProductInfo = {
  slug: 'find-a-pro-ptr',
  name: 'Find a PTR-Certified Tennis Pro',
  path: FIND_A_PRO_PTR_PAGE,
  url: productUrl(FIND_A_PRO_PTR_PAGE),
  shortDescription:
    'Find a Professional Tennis Registry (PTR) certified coach — the top tennis ' +
    'coaching certification.',
};

/* --- Editorial -------------------------------------------------------- */

export const PRODUCT_BLOG: ProductInfo = {
  slug: 'blog',
  name: 'Blog',
  path: BLOG_PAGE,
  url: productUrl(BLOG_PAGE),
  shortDescription:
    '130+ articles on rules, gear, technique, and strategy across racket sports.',
};

/* --- Section groupings (used by `/llms.txt`, `/about`, schema) -------- */

export const SOFTWARE_PRODUCTS: ProductInfo[] = [
  PRODUCT_COACHING_SOFTWARE,
  PRODUCT_CLUB_SOFTWARE,
  PRODUCT_TOURNAMENTS,
];

export const MARKETPLACE_PRODUCTS: ProductInfo[] = [
  MARKETPLACE_COURTS,
  MARKETPLACE_LESSONS,
  MARKETPLACE_CLINICS,
  MARKETPLACE_TOURNAMENTS,
  MARKETPLACE_LEAGUES,
  MARKETPLACE_SOCIALS,
  MARKETPLACE_OPEN_PLAY,
];

export const FIND_A_PRO_PRODUCTS: ProductInfo[] = [FIND_A_PRO_PPR, FIND_A_PRO_PTR];

export const EDITORIAL_PRODUCTS: ProductInfo[] = [PRODUCT_BLOG];

/**
 * Flat list — kept for back-compat with anything iterating all products.
 * Prefer the section-specific arrays above for new usages.
 */
export const PRODUCTS: ProductInfo[] = [
  ...SOFTWARE_PRODUCTS,
  ...MARKETPLACE_PRODUCTS,
  ...FIND_A_PRO_PRODUCTS,
  ...EDITORIAL_PRODUCTS,
];

/* --- Back-compat alias ------------------------------------------------ */

/**
 * @deprecated Use `MARKETPLACE_COURTS` for the marketplace surface or
 * `PRODUCT_TOURNAMENTS` for the organizer software. Kept so existing
 * callers (e.g. `screens/AboutPage`) don't break.
 */
export const PRODUCT_COURTS_DIRECTORY = MARKETPLACE_COURTS;
